// Modal that links one receipt to MANY outstanding invoices in one
// shot.
//
// Sam's rule (Feb 2026):
//   * search by client name (front-end filter on invoices we already
//     have loaded from the reconcile page — no extra API call);
//   * tick 1+ outstanding invoices;
//   * Confirm is disabled until the sum of ticked invoices' *remaining
//     owed* equals the receipt amount EXACTLY;
//   * on success every ticked invoice is marked paid.
//
// The heavy validation (invoice still owed, receipt amount matches, no
// double-linking) runs server-side in `POST /invoices/link-payment-multi`
// too — this modal is the friendly first line of defence.
import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const money = (n) =>
  `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ukDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB"); }
  catch { return iso; }
};

/**
 * Given every outstanding invoice we know about, derive its *remaining
 * owed* — the amount that must be zeroed for the invoice to flip to
 * paid. Sandra's side reconciles: total minus the sum of already-linked
 * transaction amounts.
 */
const remainingOwed = (inv) => {
  if (typeof inv.amount_owed === "number") return inv.amount_owed;
  const paid = (inv.linked_transactions || []).reduce(
    (acc, t) => acc + Number(t.amount || 0),
    0,
  );
  return Math.max(0, Number(inv.total || 0) - paid);
};

export default function LinkMultiInvoiceModal({
  open,
  onOpenChange,
  receipt,           // {transaction_id, amount, description, timestamp}
  invoices,          // outstanding invoices already loaded by ReconcilePage
  onLinked,          // () => refreshAll()
}) {
  const [search, setSearch] = useState("");
  const [ticked, setTicked] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  const receiptAmount = Number(receipt?.amount || 0);

  // Only include invoices with money still owed on them — matches
  // Sam's Q3 answer (b): "only show invoices with an amount still owed".
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (invoices || [])
      .filter((inv) => remainingOwed(inv) > 0.005)
      .filter((inv) => {
        if (!q) return true;
        return (
          (inv.client_name || "").toLowerCase().includes(q) ||
          (inv.invoice_number || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.due_date || "").localeCompare(a.due_date || ""));
  }, [invoices, search]);

  const selectedTotal = useMemo(() => {
    return (invoices || [])
      .filter((inv) => ticked.has(inv.id))
      .reduce((acc, inv) => acc + remainingOwed(inv), 0);
  }, [invoices, ticked]);

  const diff = selectedTotal - receiptAmount;
  const matchesExactly =
    Math.abs(diff) < 0.005 && ticked.size > 0 && receiptAmount > 0;

  const toggle = (id) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleClose = (isOpen) => {
    if (submitting) return;
    if (!isOpen) {
      setSearch("");
      setTicked(new Set());
    }
    onOpenChange?.(isOpen);
  };

  const confirm = async () => {
    if (!matchesExactly || submitting) return;
    setSubmitting(true);
    try {
      const invoice_ids = Array.from(ticked);
      await api.post(`/invoices/link-payment-multi`, {
        transaction_id: receipt.transaction_id,
        invoice_ids,
      });
      toast.success(
        `Receipt linked to ${invoice_ids.length} invoice${invoice_ids.length > 1 ? "s" : ""} — all marked Paid`,
      );
      handleClose(false);
      onLinked?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Link failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-2xl"
        data-testid="link-multi-modal"
      >
        <DialogHeader>
          <DialogTitle>Link receipt to invoices</DialogTitle>
        </DialogHeader>

        {/* Receipt summary */}
        <div className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider font-bold text-stone-500">
              Receipt · {ukDate(receipt?.timestamp)}
            </div>
            <div
              className="text-sm text-slate-800 truncate"
              title={receipt?.description}
              data-testid="link-multi-receipt-description"
            >
              {receipt?.description || "—"}
            </div>
          </div>
          <div
            className="font-mono text-2xl font-bold text-emerald-700 shrink-0 ml-4"
            data-testid="link-multi-receipt-amount"
          >
            {money(receiptAmount)}
          </div>
        </div>

        {/* Client search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            autoFocus
            placeholder="Search by client name or invoice number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="link-multi-search"
          />
        </div>

        {/* Filtered invoice list */}
        <div
          className="border border-stone-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto"
          data-testid="link-multi-invoice-list"
        >
          {filtered.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              {search.trim()
                ? "No outstanding invoices match that search."
                : "No outstanding invoices."}
            </div>
          )}
          <ul className="divide-y divide-stone-100">
            {filtered.map((inv) => {
              const owed = remainingOwed(inv);
              const isTicked = ticked.has(inv.id);
              return (
                <li
                  key={inv.id}
                  className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer ${isTicked ? "bg-emerald-50/70" : "hover:bg-stone-50"}`}
                  onClick={() => toggle(inv.id)}
                  data-testid={`link-multi-invoice-${inv.id}`}
                >
                  <Checkbox
                    checked={isTicked}
                    onCheckedChange={() => toggle(inv.id)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`link-multi-invoice-check-${inv.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {inv.client_name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {inv.invoice_number} · Due {ukDate(inv.due_date)}
                    </div>
                  </div>
                  <div className="font-mono text-sm font-bold text-slate-900 shrink-0">
                    {money(owed)}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Running total & mismatch warning */}
        <div className="rounded-lg border border-stone-200 px-4 py-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-stone-600">Selected invoices total</span>
            <span
              className={`font-mono font-bold ${matchesExactly ? "text-emerald-700" : "text-slate-900"}`}
              data-testid="link-multi-selected-total"
            >
              {money(selectedTotal)}
            </span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-stone-600">Receipt amount</span>
            <span
              className="font-mono font-bold text-slate-900"
              data-testid="link-multi-receipt-total"
            >
              {money(receiptAmount)}
            </span>
          </div>
          {!matchesExactly && ticked.size > 0 && (
            <div
              className="mt-2 text-xs font-semibold text-rose-600"
              data-testid="link-multi-mismatch"
            >
              {diff > 0
                ? `Selected total is ${money(diff)} too high — untick some invoices.`
                : `Selected total is ${money(-diff)} too low — tick more invoices.`}
            </div>
          )}
          {matchesExactly && (
            <div
              className="mt-2 text-xs font-semibold text-emerald-700 flex items-center gap-1"
              data-testid="link-multi-match-ok"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Totals match — Confirm will mark {ticked.size} invoice{ticked.size > 1 ? "s" : ""} paid.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={submitting}
            data-testid="link-multi-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={!matchesExactly || submitting}
            data-testid="link-multi-confirm"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Confirm & mark paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
